import {IActionDispatcher, IPaylodAction} from '../interfaces/dispatchers'
import {
  IFeatureDescriptionsByServiceType,
  ISourceDescription,
  ISourceDescriptions,
  ISourceDescriptionsByServiceType,
  ISourcesInfosPayload
} from '../interfaces/models'

import {v4 as uuidv4} from 'uuid'
import LayerInfosActions from '../enums/ActionTypes/LayerInfosActions'
import MapActions from '../enums/ActionTypes/MapActions'
import {ISourcesDispatcher} from '../interfaces/dispatchers'
import {IFeatureLayerInfo} from '../interfaces/models'
import {IStore} from '../interfaces/reducers'

import {
  Feature,
  GeoJSON,
  ImageLayer,
  Projection,
  StrategyManager,
  TileLayer,
  VectorLayer,
  VectorSource
} from '../models'

import {Extent} from 'ol/extent'
import {ViewSchemaType} from '../enums/ViewSchemaType'
import {getResolutions, getVectorLayerStyle} from '../helpers'
import {IFeatureLayerInfos} from '../interfaces/reducers'
import {IMapService} from '../interfaces/services'
import {getMapService} from '../reducers/map'
import {ServiceHub} from '../ServiceHub'

export class SourcesDispatcher implements ISourcesDispatcher {
  constructor() {
    this.clearAllData = this.clearAllData.bind(this)
    this.dispatchSources = this.dispatchSources.bind(this)
  }

  attachImageLayers(featureLayerInfos: IFeatureLayerInfos, getStore: () => IStore) {
    const sourceDescriptionsByServiceType: IFeatureDescriptionsByServiceType = {
      wfs: {},
      xyGis: {}
    }
    for (const key in featureLayerInfos) {
      if (featureLayerInfos[key].hasImageViewSchema()) {
        const s = featureLayerInfos[key].sourceDescription
        if (s.serviceType == 'wfs') {
          if (!sourceDescriptionsByServiceType.wfs[s.namespacedUrl]) {
            sourceDescriptionsByServiceType.wfs[s.namespacedUrl] = {}
          }
          sourceDescriptionsByServiceType.wfs[s.namespacedUrl][s.id] = featureLayerInfos[key]
        } else if (s.serviceType == 'xyGis') {
          if (!sourceDescriptionsByServiceType.xyGis[s.namespacedUrl]) {
            sourceDescriptionsByServiceType.xyGis[s.namespacedUrl] = {}
          }
          sourceDescriptionsByServiceType.xyGis[s.namespacedUrl][s.id] = featureLayerInfos[key]
        } else {
          throw new Error('not implemented')
        }
      }
    }
    let layers: Array<TileLayer | ImageLayer> = []
    layers = layers.concat(
      ServiceHub.WFSLayerInfosService.createImageLayers(sourceDescriptionsByServiceType.wfs, getStore)
    )
    layers = layers.concat(
      ServiceHub.WFSLayerInfosService.createImageLayers(sourceDescriptionsByServiceType.xyGis, getStore)
    )
    layers.forEach(l => {
      for (const key in featureLayerInfos) {
        if (l.moreInfo.urls.indexOf(key) != -1) {
          featureLayerInfos[key].imageLayer = l
        }
      }
    })
  }

  protected getLoaderFunction(
    layerInfo: IFeatureLayerInfo,
    mapService: IMapService,
    getStore: () => IStore,
    dispatch: (data: IPaylodAction | IActionDispatcher) => void
  ) {
    const prjCode = mapService.getCode()
    let loaderFunction: (extent: Extent, resolution: number, projection: Projection) => Promise<Array<Feature>> =
      undefined as any
    if (layerInfo.sourceDescription.serviceType == 'wfs') {
      loaderFunction = ServiceHub.WFSLayerInfosService.getVectorLoadFunction(layerInfo, prjCode, getStore, dispatch)
    } else if (layerInfo.sourceDescription.serviceType == 'xyGis') {
      loaderFunction = ServiceHub.XyGisLayerInfosService.getVectorLoadFunction(layerInfo, getStore, dispatch)
    }
    return loaderFunction
  }

  protected createVectorLayer(
    layerInfo: IFeatureLayerInfo,
    mapService: IMapService,
    getStore: () => IStore,
    dispatch: (data: IPaylodAction | IActionDispatcher) => void
  ) {
    const loaderFunction = this.getLoaderFunction(layerInfo, mapService, getStore, dispatch)
    if (!loaderFunction) {
      throw new Error('No loading function')
    }
    const source = new VectorSource({
      format: new GeoJSON(),
      loader: (extent: any, resolution: any, projection: any) => {
        if (layerInfo.sourceDescription.nonserver) {
          return
        }
        loaderFunction(extent, resolution, projection).then(features => {
          if (features.length > 0) {
            source.addFeatures(features)
          }
        })
      },
      strategy: StrategyManager.bbox
    })

    return new VectorLayer({
      source: source,
      visible: layerInfo.sourceDescription.visible,
      opacity: layerInfo.sourceDescription.opacity
    })
  }

  protected attachVectorLayers(
    featureLayerInfos: IFeatureLayerInfos,
    mapService: IMapService,
    getStore: () => IStore,
    dispatch: (data: IPaylodAction | IActionDispatcher) => void
  ): void {
    for (const layerUrl in featureLayerInfos) {
      const layerInfo = featureLayerInfos[layerUrl]
      const vectorLayer = this.createVectorLayer(layerInfo, mapService, getStore, dispatch)
      let maxRes = 0
      let minRes = 0
      for (const zLevel in layerInfo.viewSchema) {
        const intZLevel = parseInt(zLevel)
        if (getResolutions()[intZLevel] && layerInfo.viewSchema[zLevel] == ViewSchemaType.Vector) {
          if (!maxRes) {
            if (intZLevel == 0) {
              maxRes = getResolutions()[intZLevel] + 0.001
            } else {
              maxRes = getResolutions()[intZLevel - 1] - 0.002
            }
          }
          minRes = getResolutions()[intZLevel] - 0.001
        }
      }
      vectorLayer.setMaxResolution(maxRes)
      vectorLayer.setMinResolution(minRes)
      vectorLayer.moreInfo = {
        urls: [layerInfo.sourceDescription.id],
        guid: uuidv4(),
        type: 'regular',
        refreshOnExtentChange: layerInfo.sourceDescription.refreshOnExtentChange,
        isActive: true
      }

      vectorLayer.setStyle(getVectorLayerStyle(layerInfo, mapService) as any)
      vectorLayer.setZIndex(layerInfo.sourceDescription.z || 1)
      layerInfo.vectorLayer = vectorLayer
    }
  }

  protected groupByServiceType(sourceDescriptions: ISourceDescriptions): ISourceDescriptionsByServiceType {
    const result: ISourceDescriptionsByServiceType = {
      xyGis: {},
      wfs: {}
    }
    for (const sourceUrl in sourceDescriptions) {
      const sourceDescription = sourceDescriptions[sourceUrl]
      if (sourceDescription.serviceType == 'wfs') {
        if (!result.wfs[sourceDescriptions[sourceUrl].namespacedUrl]) {
          result.wfs[sourceDescriptions[sourceUrl].namespacedUrl] = {}
        }
        result.wfs[sourceDescriptions[sourceUrl].namespacedUrl][sourceUrl] = sourceDescription
      } else if (sourceDescription.serviceType == 'xyGis') {
        if (!result.xyGis[sourceDescriptions[sourceUrl].namespacedUrl]) {
          result.xyGis[sourceDescriptions[sourceUrl].namespacedUrl] = {}
        }
        result.xyGis[sourceDescriptions[sourceUrl].namespacedUrl][sourceUrl] = sourceDescription
      } else {
        throw new Error(`${sourceDescription.serviceType} type not implemented`)
      }
    }

    return result
  }

  protected addLayersOnTheMap(featureLayerInfos: IFeatureLayerInfos, mapService: IMapService) {
    const uniqueLayerIds = {
      ...mapService.getLayers().map(l => {
        return {[l.moreInfo.guid]: {}}
      })
    }
    for (const key in featureLayerInfos) {
      if (featureLayerInfos[key].sourceDescription.addOnMap) {
        if (featureLayerInfos[key].hasImageViewSchema()) {
          if (!uniqueLayerIds[(featureLayerInfos as any)[key].imageLayer.moreInfo.guid]) {
            mapService.addLayer(featureLayerInfos[key].imageLayer)
            uniqueLayerIds[(featureLayerInfos as any)[key].imageLayer.moreInfo.guid] = {}
          }
        }
        if (featureLayerInfos[key].hasVectorViewSchema()) {
          if (!uniqueLayerIds[(featureLayerInfos as any)[key].vectorLayer.moreInfo.guid]) {
            mapService.addLayer(featureLayerInfos[key].vectorLayer)
            uniqueLayerIds[(featureLayerInfos as any)[key].vectorLayer.moreInfo.guid] = {}
          }
        }
      }
    }
  }

  public clearAllData() {
    return (dispatch: (data: IPaylodAction) => void, getStore: () => IStore) => {
      const mapService = getStore().map.mapService
      const layers = mapService.getLayers()
      layers.forEach(l => {
        if (l && l.moreInfo.type == 'regular') {
          mapService.removeLayer(l)
        }
      })
      dispatch({
        type: LayerInfosActions.CLEAR_ALL_DATA,
        payload: {}
      })
      dispatch({
        type: MapActions.SOURCES_IMPORTED,
        payload: false
      })
    }
  }

  public dispatchSources(payload: ISourcesInfosPayload) {
    return (dispatch: (data: IPaylodAction | IActionDispatcher) => void, getStore: () => IStore) => {
      try {
        const featureLayerInfosOnTheMap: {[id: string]: IFeatureLayerInfo} = {}
        const sourceDescriptions: ISourceDescription[] = []
        for (const key in payload.featureLayerInfos) {
          const layerInfo = payload.featureLayerInfos[key]
          sourceDescriptions.push(layerInfo.sourceDescription)
          if (layerInfo.sourceDescription.addOnMap && layerInfo.sourceDescription.visible) {
            featureLayerInfosOnTheMap[layerInfo.url] = layerInfo
          }
        }
        const mapService = getMapService()
        if (mapService) {
          this.attachVectorLayers(payload.featureLayerInfos, mapService, getStore, dispatch)
          this.attachImageLayers(payload.featureLayerInfos, getStore)
          this.addLayersOnTheMap(payload.featureLayerInfos, mapService)
        } else {
          console.warn('dispatchSources: mapService is not available yet')
        }
        dispatch({
          type: LayerInfosActions.ADD_LAYER_INFOS,
          payload: payload
        })
        dispatch({
          type: MapActions.SOURCES_IMPORTED,
          payload: true
        })
        dispatch({
          type: LayerInfosActions.ADD_VISIBLE_LAYERS,
          payload: featureLayerInfosOnTheMap
        })
        dispatch({
          type: LayerInfosActions.VECTOR_LEGEND_IMAGES_LOADED,
          payload: true
        })
      } catch (error) {
        console.error('dispatchSources error:', error)
        // Still mark as imported to prevent infinite loading state
        dispatch({
          type: MapActions.SOURCES_IMPORTED,
          payload: true
        })
      }
    }
  }

  public updateLayerInfos(layerInfos: IFeatureLayerInfos) {
    return (dispatch: (data: IPaylodAction | IActionDispatcher) => void, getStore: () => IStore) => {
      dispatch({
        type: LayerInfosActions.ADD_LAYER_INFOS,
        payload: {
          featureLayerInfos: layerInfos,
          failedServices: {},
          deniedServices: {}
        }
      })
    }
  }
}
